<template>
  <section>
    <form
      class="flex rounded-xl bg-gray-300 ring-[3px] ring-green-600"
      @submit.prevent="submitForm"
    >
      <div class="flex flex-1 divide-x divide-gray-50">
        <TownFromField
          :is-active="
            searchFormFieldStore.isActiveFormField(
              SearchFormField.TOWN_TO_FORM_FIELD,
            )
          "
          :selected-town-from="searchForm.townFrom"
          :active-form-field="searchFormFieldStore.activeFormField"
          @onFieldSelect="
            onActiveFieldChange(SearchFormField.TOWN_TO_FORM_FIELD)
          "
          @onClickOutsideField="searchFormFieldStore.resetActiveFormField"
          @onTownFromSelect="onTownFromSelect"
        />
        <ArrivalField
          :is-active="
            searchFormFieldStore.isActiveFormField(
              SearchFormField.ARRIVAL_FORM_FIELD,
            )
          "
          :active-form-field="searchFormFieldStore.activeFormField"
          :town-from-id="searchForm.townFrom?.id"
          :country-to="searchForm.countryTo"
          :towns-to="searchForm.townsTo"
          @onCountryToSelect="onCountryToSelect"
          @onTownsToChange="onTownsToChange"
          @onActiveFieldChange="onActiveFieldChange"
          @onResetActiveField="searchFormFieldStore.resetActiveFormField"
          @onSkipTownsToStep="
            onActiveFieldChange(SearchFormField.DATE_FORM_FIELD)
          "
        />
        <DatepickerField
          :is-active="
            searchFormFieldStore.isActiveFormField(
              SearchFormField.DATE_FORM_FIELD,
            )
          "
          :field-value="prettySelectedDates"
          :active-form-field="searchFormFieldStore.activeFormField"
          :town-from-id="searchForm.townFrom?.id"
          :country-to-id="searchForm.countryTo?.id"
          :has-towns-to="Boolean(searchForm.townsTo)"
          :arr-date="searchForm.arrDate"
          :dep-date="searchForm.depDate"
          @onDatesChange="onDatesChange"
          @onActiveFieldChange="onActiveFieldChange"
          @onResetActiveField="searchFormFieldStore.resetActiveFormField"
          @onResetDates="searchForm.resetDatepickerState()"
        />
        <PassengersField
          :is-active="
            searchFormFieldStore.isActiveFormField(
              SearchFormField.PASSENGERS_COUNT_FORM_FIELD,
            )
          "
          :passengers="searchForm.passengers"
          :is-max-adult-count="searchForm.isMaxAdultCount"
          :is-max-children-count="searchForm.isMaxChildrenCount"
          :is-max-passengers-count="searchForm.isMaxPassengersCount"
          @onError="logPassengersError"
          @onAdultsChange="onAdultsChange"
          @onChildrenChange="onChildrenChange"
          @onClickOutsideField="searchFormFieldStore.resetActiveFormField"
          @onFieldSelect="
            onActiveFieldChange(SearchFormField.PASSENGERS_COUNT_FORM_FIELD)
          "
        />
      </div>
      <div class="flex w-[200px] overflow-hidden rounded-r-xl">
        <button
          type="submit"
          class="w-full bg-green-600 text-xl font-bold text-white duration-200 hover:bg-green-700 active:bg-green-800"
        >
          {{ $t('search-form.find-tours-short') }}
        </button>
      </div>
    </form>
  </section>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, watch } from 'vue';
  import { isEqual } from 'lodash-es';
  import { useVuelidate } from '@vuelidate/core';
  import { useTranslation } from 'i18next-vue';
  import dayjs from 'dayjs';
  import type { Tourflow } from '@aviata/platform-types';

  import { Analytics } from '@shared/analytics';
  import { SearchParams } from '@entities/search-params';
  import { Sentry } from '@shared/sentry';
  import { FLEXIBLE_DATES_RANGE, getAfterDate } from '@entities/flexible-dates';
  import { DateLib } from '@shared/lib';
  import { analyticsEvents } from '@widgets/search-form/config';
  import { lastSearchLib, type LastSearch } from '@features/save-last-search';
  import type { SelectedDates } from '@entities/dates-picker';
  import type { PassengerChildrenMap } from '@entities/passengers-state';

  import { TownFromField } from './town-from-field';
  import { ArrivalField } from './arrival-field';
  import { DatepickerField } from './date-field';
  import { PassengersField } from './passengers-field';
  import {
    useSearchFormStore,
    useSearchFormValidation,
    useSearchFormFieldStore,
  } from '../../model';
  import { SearchFormField } from '../../types';

  const props = withDefaults(
    defineProps<{
      shouldLoadLastSearch?: boolean;
      searchData?: LastSearch;
    }>(),
    { shouldLoadLastSearch: true },
  );

  const emit = defineEmits<{
    onSubmit: [searchParams: SearchParams];
  }>();

  const { t } = useTranslation();
  const searchFormFieldStore = useSearchFormFieldStore();
  const searchForm = useSearchFormStore();
  const searchFormValidation = useVuelidate(
    useSearchFormValidation('simple-flow.main.search-form.validations'),
    computed(() => searchForm),
  );

  const prettySelectedDates = computed(() => {
    if (
      !searchForm.depDate?.from ||
      !searchForm.arrDate?.from ||
      !searchForm.nightsDifferenceCount
    ) {
      return '';
    }

    const depDate = searchForm.isFlexibleDates
      ? getAfterDate(searchForm.depDate.from, FLEXIBLE_DATES_RANGE)
      : searchForm.depDate.from;
    const arrDate = searchForm.isFlexibleDates
      ? getAfterDate(searchForm.arrDate.from, FLEXIBLE_DATES_RANGE)
      : searchForm.arrDate.from;

    const isSameMonth = dayjs(depDate).isSame(arrDate, 'M');

    const prettyArr = DateLib.prettyDate(arrDate, 'D MMM');
    const prettyDep = isSameMonth
      ? DateLib.prettyDate(depDate, 'D')
      : DateLib.prettyDate(depDate, 'D MMM');

    const selectedDatesText = `${prettyDep} — ${prettyArr}`;

    const nightsText = t('simple-flow.general.nights-amount', {
      count: searchForm.nightsDifferenceCount,
      ns: 'translation',
    });

    return `${selectedDatesText} ${
      searchForm.isFlexibleDates
        ? t('search-form.dates.selected-flexible-dates', {
            dateRange: FLEXIBLE_DATES_RANGE,
          })
        : `, ${nightsText}`
    }`;
  });

  const onActiveFieldChange = (field: SearchFormField) => {
    searchFormFieldStore.setActiveFormField(field);
  };

  const onTownFromSelect = (town: Tourflow.TownFrom) => {
    searchForm.onTownFromChange(town);
    searchFormFieldStore.setActiveFormField(SearchFormField.ARRIVAL_FORM_FIELD);

    Analytics.logEvent({
      event: analyticsEvents.TOWN_FROM.SELECT,
      action: {
        townFromName: town.name,
      },
    });
  };

  const onCountryToSelect = (country?: Tourflow.CountryTo) => {
    searchForm.onCountryToSelect(country);

    Analytics.logEvent({
      event: analyticsEvents.TOWNS_TO.COUNTRY_TO_CLICK,
      action: {
        country: country?.name,
      },
    });
  };

  const onTownsToChange = (townsTo: Tourflow.TownTo[] | null) => {
    searchForm.onTownsToChange(townsTo);

    Analytics.logEvent({
      event: analyticsEvents.TOWNS_TO.CLICK,
      action: { towns: townsTo?.map(({ name }) => name) },
    });

    const isAllTownsToSelected =
      !searchForm.townsTo || searchForm.townsTo.length === 0;

    Analytics.logEvent({
      event: analyticsEvents.COUNTRY_TO.SELECT,
      action: {
        townsTo: isAllTownsToSelected
          ? 'all'
          : searchForm.townsTo?.map(({ name }) => name),
        country: searchForm.countryTo?.name,
      },
    });
  };

  const onDatesChange = ([depDate, arrDate]: SelectedDates[]) => {
    searchForm.setDepDate(depDate);
    searchForm.setArrDate(arrDate);

    Analytics.logEvent({
      event: analyticsEvents.DATES.SELECT,
      action: {
        dates: [String(depDate), String(arrDate)],
      },
    });
  };

  const logPassengersChange = () => {
    Analytics.logEvent({
      event: analyticsEvents.PASSENGERS.SELECT,
      action: {
        ADT: searchForm.passengers.ADT,
        CHD: searchForm.passengers.CHD,
        INF: searchForm.passengers.INF,
      },
    });
  };

  const logPassengersError = (error: string) => {
    Analytics.logEvent({
      event: analyticsEvents.PASSENGERS.INVALID,
      action: { error },
    });
  };

  const onAdultsChange = (count: number) => {
    searchForm.setAdults(count);
    logPassengersChange();
  };

  const onChildrenChange = (children: PassengerChildrenMap) => {
    searchForm.setChildren(children);
    logPassengersChange();
  };

  const saveLastSearch = (currentSearch: LastSearch) => {
    const lastSearch = lastSearchLib.get();
    lastSearchLib.set([currentSearch]);

    if (isEqual(lastSearch, currentSearch)) {
      Analytics.logEvent({
        event: analyticsEvents.START_SEARCH_WITH_OLD_DATA,
      });
    }
  };

  const setFirstInvalidFieldActive = () => {
    if (!searchForm.townFrom) {
      searchFormFieldStore.setActiveFormField(
        SearchFormField.TOWN_TO_FORM_FIELD,
      );
    } else if (searchForm.townsTo === null) {
      searchFormFieldStore.setActiveFormField(
        SearchFormField.ARRIVAL_FORM_FIELD,
      );
    } else if (!searchForm.depDate.from && !searchForm.arrDate.from) {
      searchFormFieldStore.setActiveFormField(SearchFormField.DATE_FORM_FIELD);
    } else if (!searchForm.hasPassengers) {
      searchFormFieldStore.setActiveFormField(
        SearchFormField.PASSENGERS_COUNT_FORM_FIELD,
      );
    }
  };

  const submitForm = () => {
    searchFormValidation.value.$touch();

    if (searchFormValidation.value.$invalid) {
      const { $errors } = searchFormValidation.value;

      Analytics.logEvent({
        event: analyticsEvents.INVALID,
        action: $errors.reduce((acc, { $propertyPath, $message }) => {
          if (acc[$propertyPath]) {
            acc[$propertyPath].push($message.toString());
          } else {
            acc[$propertyPath] = [$message.toString()];
          }
          return acc;
        }, {} as Record<string, string[]>),
      });

      setFirstInvalidFieldActive();
    } else if (
      searchForm.townFrom &&
      searchForm.countryTo &&
      searchForm.depDate &&
      searchForm.arrDate &&
      searchForm.nightsDifferenceCount
    ) {
      const searchParams = new SearchParams({
        townFrom: searchForm.townFrom.id,
        countryTo: searchForm.countryTo.id,
        townsTo: searchForm.townsTo?.map(({ id }) => id) || [],
        adt: searchForm.passengers.ADT,
        childrenAge: searchForm.childrenAge,
        depFromDate: searchForm.depDate.from,
        depToDate: searchForm.depDate.to,
        nights: searchForm.nightsDifferenceCount,
        nightsTo: searchForm.nightsDifferenceCount,
      });

      saveLastSearch({
        townsTo: searchForm.townsTo,
        countryTo: searchForm.countryTo,
        townFrom: searchForm.townFrom,
        depDate: searchForm.depDate,
        arrDate: searchForm.arrDate,
        adt: searchForm.passengers.ADT,
        childrenAge: searchForm.childrenAge,
      });

      emit('onSubmit', searchParams);
    } else {
      Sentry.captureExWithCause('ERR_INVALID_FORM_DATA', {
        captureContext: {
          extra: { searchForm },
        },
      });
    }
  };

  const setSearchData = (search: LastSearch) => {
    searchForm.townFrom = search.townFrom;
    searchForm.townsTo = search.townsTo;
    searchForm.countryTo = search.countryTo;
    searchForm.setDepDate(search.depDate);
    searchForm.setArrDate(search.arrDate);
    searchForm.setAdults(search.adt);
    searchForm.setChildrenWithAges(search.childrenAge);
  };

  watch(
    () => props.searchData,
    data => {
      if (data) {
        setSearchData(data);
      }
    },
  );

  onBeforeMount(() => {
    const lastSearch = lastSearchLib.get();

    if (props.shouldLoadLastSearch && lastSearch) {
      setSearchData(lastSearch[0]);
    }
  });
</script>
